import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  FileText,
  Folder,
  Copy,
  Check,
  Filter,
  X,
  HardDrive,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatBytes, formatDate, formatDuration, formatNumber } from "../lib/format";
import { cn } from "../lib/cn";
import type { DiskAggregateRow, SearchResultRow } from "../types";

const COMMON_EXT = ["mov", "mp4", "mxf", "r3d", "braw", "psd", "ai", "aep", "prproj", "wav", "mp3", "jpg", "png", "tif"];
const PAGE_SIZE = 50;

type ViewMode = "files" | "disks";

export default function SearchPage(): JSX.Element {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [extFilters, setExtFilters] = useState<string[]>([]);
  const [diskFilter, setDiskFilter] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState<ViewMode>("files");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Réinitialise la pagination quand la recherche/filtres changent
  useEffect(() => {
    setPage(0);
  }, [debounced, extFilters, diskFilter, view]);

  const { data: disks = [] } = useQuery({
    queryKey: ["disks"],
    queryFn: () => api.listDisks(),
  });

  const enabled = debounced.trim().length >= 2;

  const filesQuery = useQuery({
    queryKey: ["search-files", debounced, extFilters, diskFilter, page],
    queryFn: () =>
      api.search({
        q: debounced.trim(),
        ext: extFilters.length ? extFilters : undefined,
        diskId: diskFilter || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    enabled: enabled && view === "files",
    placeholderData: (prev) => prev,
  });

  const disksQuery = useQuery({
    queryKey: ["search-disks", debounced, extFilters],
    queryFn: () =>
      api.searchByDisks({
        q: debounced.trim(),
        ext: extFilters.length ? extFilters : undefined,
      }),
    enabled: enabled && view === "disks",
  });

  const onPickDisk = (diskId: string): void => {
    setDiskFilter(diskId);
    setView("files");
  };

  return (
    <div className="space-y-8">
      <section>
        <div className="mb-2 label-caps">Recherche</div>
        <h1 className="title-dual text-h1">
          Retrouve un fichier <span className="dim">sur n'importe quel disque</span>
        </h1>
        <p className="mt-3 text-foreground/60 text-lead max-w-2xl">
          Tape un nom de fichier, un fragment de chemin, un projet. La recherche couvre tous les disques indexés.
        </p>
      </section>

      <section className="card-koloc !p-0 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4">
          <Search className="h-5 w-5 text-foreground/50 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ex: PUB_AIRBNB_2024 ou .mov"
            className="flex-1 bg-transparent text-lead placeholder:text-foreground/30 focus:outline-none"
            autoFocus
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="grid h-7 w-7 place-items-center rounded-pill text-foreground/50 hover:bg-foreground/10 transition-all"
              aria-label="Effacer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={cn(
              "inline-flex items-center gap-2 rounded-pill px-3 py-1.5 text-caption transition-all",
              showFilters ? "bg-foreground text-background" : "text-foreground/60 hover:bg-foreground/5"
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            Filtres
            {(extFilters.length > 0 || diskFilter) && (
              <span className="ml-1 grid h-4 min-w-4 place-items-center rounded-full bg-background/20 px-1 text-[10px]">
                {extFilters.length + (diskFilter ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="border-t border-foreground/8 px-5 py-4 space-y-4 animate-slide-up">
            <div>
              <div className="label-caps mb-2">Extensions</div>
              <div className="flex flex-wrap gap-2">
                {COMMON_EXT.map((e) => {
                  const active = extFilters.includes(e);
                  return (
                    <button
                      key={e}
                      onClick={() =>
                        setExtFilters((cur) => (active ? cur.filter((x) => x !== e) : [...cur, e]))
                      }
                      className={cn(
                        "rounded-pill px-3 py-1 text-caption transition-all",
                        active
                          ? "bg-foreground text-background"
                          : "border border-foreground/15 text-foreground/70 hover:bg-foreground/5"
                      )}
                    >
                      .{e}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="label-caps mb-2">Disque</div>
              <div className="flex items-center gap-2">
                <select
                  value={diskFilter}
                  onChange={(e) => setDiskFilter(e.target.value)}
                  className="input-koloc max-w-md"
                >
                  <option value="">Tous les disques</option>
                  {disks.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label} ({d.fileCount.toLocaleString("fr-CA")} fichiers)
                    </option>
                  ))}
                </select>
                {diskFilter && (
                  <button
                    onClick={() => setDiskFilter("")}
                    className="text-caption text-foreground/50 hover:text-foreground"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {!enabled && (
        <EmptyState
          title="Tape au moins 2 caractères"
          subtitle="On cherche dans le nom et le chemin de tous les fichiers indexés."
        />
      )}

      {enabled && <ViewTabs view={view} onChange={setView} />}

      {enabled && view === "files" && (
        <ResultsSection
          loading={filesQuery.isFetching}
          error={filesQuery.error as Error | null}
          total={filesQuery.data?.total ?? 0}
          tookMs={filesQuery.data?.tookMs ?? 0}
          rows={filesQuery.data?.rows ?? []}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {enabled && view === "disks" && (
        <DisksSection
          loading={disksQuery.isFetching}
          error={disksQuery.error as Error | null}
          rows={disksQuery.data?.rows ?? []}
          tookMs={disksQuery.data?.tookMs ?? 0}
          onPick={onPickDisk}
        />
      )}
    </div>
  );
}

function ViewTabs({
  view,
  onChange,
}: {
  view: ViewMode;
  onChange: (v: ViewMode) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1 rounded-pill border border-foreground/10 bg-foreground/[0.02] p-1">
      <TabButton active={view === "files"} onClick={() => onChange("files")} icon={<FileText className="h-3.5 w-3.5" />}>
        Fichiers
      </TabButton>
      <TabButton active={view === "disks"} onClick={() => onChange("disks")} icon={<HardDrive className="h-3.5 w-3.5" />}>
        Disques
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-pill px-3.5 py-1.5 text-caption transition-all",
        active
          ? "bg-foreground text-background"
          : "text-foreground/60 hover:bg-foreground/5 hover:text-foreground/90"
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function ResultsSection({
  loading,
  error,
  total,
  tookMs,
  rows,
  page,
  pageSize,
  onPageChange,
}: {
  loading: boolean;
  error: Error | null;
  total: number;
  tookMs: number;
  rows: SearchResultRow[];
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
}): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  if (error) {
    return (
      <div className="card-koloc border-destructive/20 text-destructive">
        Recherche impossible : {error.message}
      </div>
    );
  }

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between">
        <div className="label-caps">
          {loading && rows.length === 0
            ? "Recherche en cours"
            : `${total.toLocaleString("fr-CA")} ${total <= 1 ? "résultat" : "résultats"}`}
        </div>
        {!loading && tookMs > 0 && (
          <div className="text-caption text-foreground/40 num">{formatDuration(tookMs)}</div>
        )}
      </div>

      {loading && rows.length === 0 ? (
        <SkeletonTable />
      ) : rows.length === 0 ? (
        <EmptyState title="Aucun résultat" subtitle="Essaie un terme plus large ou retire des filtres." />
      ) : (
        <>
          <ResultsTable rows={rows} />
          {total > pageSize && (
            <Pagination
              page={page}
              totalPages={totalPages}
              from={from}
              to={to}
              total={total}
              onPageChange={onPageChange}
              loading={loading}
            />
          )}
        </>
      )}
    </section>
  );
}

function Pagination({
  page,
  totalPages,
  from,
  to,
  total,
  onPageChange,
  loading,
}: {
  page: number;
  totalPages: number;
  from: number;
  to: number;
  total: number;
  onPageChange: (p: number) => void;
  loading: boolean;
}): JSX.Element {
  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <div className="text-caption text-foreground/50 num">
        {from.toLocaleString("fr-CA")}–{to.toLocaleString("fr-CA")} sur {total.toLocaleString("fr-CA")}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0 || loading}
          className="pill-outline pill-sm"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Préc.
        </button>
        <div className="text-caption text-foreground/60 num px-2">
          {page + 1} / {totalPages}
        </div>
        <button
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1 || loading}
          className="pill-outline pill-sm"
        >
          Suiv.
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function DisksSection({
  loading,
  error,
  rows,
  tookMs,
  onPick,
}: {
  loading: boolean;
  error: Error | null;
  rows: DiskAggregateRow[];
  tookMs: number;
  onPick: (diskId: string) => void;
}): JSX.Element {
  if (error) {
    return (
      <div className="card-koloc border-destructive/20 text-destructive">
        Recherche impossible : {error.message}
      </div>
    );
  }

  const totalFiles = rows.reduce((acc, r) => acc + r.fileCount, 0);

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between">
        <div className="label-caps">
          {loading && rows.length === 0
            ? "Agrégation en cours"
            : `${rows.length} ${rows.length <= 1 ? "disque" : "disques"} · ${formatNumber(totalFiles)} fichiers`}
        </div>
        {!loading && tookMs > 0 && (
          <div className="text-caption text-foreground/40 num">{formatDuration(tookMs)}</div>
        )}
      </div>

      {loading && rows.length === 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card-koloc h-32 shimmer rounded-md" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Aucun disque ne contient ce terme"
          subtitle="Essaie un terme plus large ou retire des filtres."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((r) => (
            <button
              key={r.diskId}
              onClick={() => onPick(r.diskId)}
              className="card-koloc card-lift text-left flex flex-col gap-4 group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 grid h-10 w-10 place-items-center rounded-md bg-foreground/8">
                    <HardDrive className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-body font-semibold">{r.diskLabel}</div>
                    <div className="truncate text-caption text-foreground/50 num">
                      Scanné {formatDate(r.diskScannedAt)}
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-foreground/30 group-hover:text-foreground/80 transition-colors shrink-0" />
              </div>

              <div className="grid grid-cols-3 gap-3 text-caption">
                <div>
                  <div className="label-caps">Fichiers</div>
                  <div className="mt-1 num text-body">{formatNumber(r.fileCount)}</div>
                </div>
                <div>
                  <div className="label-caps">Taille</div>
                  <div className="mt-1 num text-body">{formatBytes(r.totalSize)}</div>
                </div>
                <div>
                  <div className="label-caps">Modifié</div>
                  <div className="mt-1 num text-body">{formatDate(r.lastModified)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function ResultsTable({
  rows,
}: {
  rows: SearchResultRow[];
}): JSX.Element {
  const [copied, setCopied] = useState<number | null>(null);

  const onCopy = async (id: number, text: string): Promise<void> => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1200);
  };

  return (
    <div className="overflow-hidden rounded-md border border-foreground/8 bg-surface">
      <table className="w-full">
        <thead>
          <tr className="border-b border-foreground/8">
            <Th className="w-[44%]">Nom · Chemin</Th>
            <Th>Disque</Th>
            <Th className="text-right">Taille</Th>
            <Th>Modifié</Th>
            <Th className="w-12"></Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-foreground/8 last:border-0 hover:bg-foreground/[0.02] transition-colors"
            >
              <Td>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="mt-0.5 shrink-0 text-foreground/40">
                    {r.isDirectory ? <Folder className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-body font-medium">{r.name}</div>
                    <div className="truncate text-caption text-foreground/50 font-mono">{r.path}</div>
                  </div>
                </div>
              </Td>
              <Td>
                <Link to={`/disques/${r.diskId}`} className="badge hover:bg-foreground/12 transition-colors">
                  {r.diskLabel}
                </Link>
              </Td>
              <Td className="text-right num text-foreground/70">{formatBytes(r.sizeBytes)}</Td>
              <Td className="text-foreground/60 num">{formatDate(r.modifiedAtFs)}</Td>
              <Td>
                <button
                  onClick={() => onCopy(r.id, r.path)}
                  className="grid h-7 w-7 place-items-center rounded-pill text-foreground/40 hover:text-foreground hover:bg-foreground/8 transition-all"
                  aria-label="Copier le chemin"
                >
                  {copied === r.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }): JSX.Element {
  return (
    <th
      className={cn(
        "px-4 py-3 text-left label-caps text-foreground/50 font-medium",
        className
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }): JSX.Element {
  return <td className={cn("px-4 py-3 text-body align-top", className)}>{children}</td>;
}

function SkeletonTable(): JSX.Element {
  const rows = useMemo(() => Array.from({ length: 6 }), []);
  return (
    <div className="overflow-hidden rounded-md border border-foreground/8 bg-surface">
      {rows.map((_, i) => (
        <div key={i} className="border-b border-foreground/8 last:border-0 p-4">
          <div className="shimmer h-4 w-2/3 rounded" />
          <div className="shimmer mt-2 h-3 w-1/3 rounded" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, subtitle }: { title: string; subtitle: string }): JSX.Element {
  return (
    <div className="card-koloc text-center py-16">
      <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-pill bg-foreground/5">
        <Search className="h-5 w-5 text-foreground/40" />
      </div>
      <h3 className="text-h3 font-semibold">{title}</h3>
      <p className="mt-2 text-foreground/50 text-body max-w-md mx-auto">{subtitle}</p>
    </div>
  );
}
